import { FC, useEffect, useState } from 'react'
import { Box } from '@/components/atoms/Grid'
import {
  Desktop,
  MediaContextProvider,
  Mobile,
  Tablet
} from '@/components/atoms/media'
import ProductLabels, {
  ProductLabelStyle
} from '@/components/organisms/productLabels'
import FullscreenGallery from '@/components/organisms/fullscreenGallery'
import ImageBrowserDesktop from './imageBrowserDesktop'
import {
  ImageLabel,
  Product,
  ProductLabelType,
  TextLabel
} from '@/network/graphql.g'
import styled, { css } from 'styled-components'
import { BreakpointKey } from '@/theme/breakpoints'
import ProductDetailImageBrowser, {
  LazyMode
} from './productDetailImageBrowser'
import { applyImagePolicy, ImageMode } from '@/utils/imageExtension'
import { filterBadges } from '@/components/molecules/productBadge'
import { useRouter } from 'next/router'
import { useAsset } from '@/providers/assetsProvider'
import { calculateDiscount } from '@/core/toStringHelper'
import { ProductImageArea } from '@/core/productImageReporter'
import { isThumborEnabledForProduct } from '@/utils/productImage'

type ImagesBrowserProps = {
  modalMode?: boolean
  imageUrls: string[]
  initialIndex?: number
  badges: ImageLabel[]
  labels: TextLabel[]
  showBullets?: boolean
  hoverColorVariant: string
  product: Product
  modalImageArea?: ProductImageArea
} & Rest

const ImagesBrowser: FC<ImagesBrowserProps> = ({
  modalMode = false,
  imageUrls,
  initialIndex = 0,
  badges,
  labels,
  showBullets = true,
  hoverColorVariant,
  product,
  modalImageArea = 'quickview',
  ...rest
}) => {
  const router = useRouter()
  const useThumbor = isThumborEnabledForProduct(product?.id)
  const [showFullscreenModal, setShowFullscreenModal] = useState<{
    show: boolean
    imageIndex: number
  }>({ show: false, imageIndex: initialIndex })
  const onImageClick = (imageIndex) => {
    if (modalMode) {
      return
    }
    setShowFullscreenModal({ show: true, imageIndex: imageIndex })
  }

  const { useSOIPricing } = useAsset()

  const hasPromoPrice = (product?.prices?.promoCodePrice || 0) > 0

  const discountBestPrice = hasPromoPrice
    ? calculateDiscount(
        product?.prices?.promoCodePrice,
        product?.prices?.minLastMonthPrice
      )
    : calculateDiscount(
        product?.prices?.finalPrice,
        product?.prices?.minLastMonthPrice
      )

  const isPriceHigherWithPromo =
    product?.prices?.minLastMonthPrice < product?.prices?.finalPrice

  const productLabelsProps = {
    badges: filterBadges(badges, false),
    labels: useSOIPricing
      ? discountBestPrice === 0 || isPriceHigherWithPromo
        ? labels.filter((label) => label.type !== ProductLabelType.Discount)
        : labels.map((label) => {
            if (label.type === ProductLabelType.Discount) {
              return {
                ...label,
                title: `${discountBestPrice}%`
              }
            }
            return label
          })
      : labels,
    labelStyle: ProductLabelStyle.Detail
  }

  const topRightProductLabelsProps = {
    badges: filterBadges(badges, true),
    labels: [],
    labelStyle: ProductLabelStyle.Detail
  }

  const blockBackRouter = () => {
    history.pushState(null, '', router.asPath)
    router.push(router.asPath)
    setShowFullscreenModal({ ...showFullscreenModal, show: false })
  }

  useEffect(() => {
    if (showFullscreenModal.show) {
      window.addEventListener('popstate', blockBackRouter)
    }
    return () => {
      window.removeEventListener('popstate', blockBackRouter)
    }
  }, [showFullscreenModal.show, router.asPath])

  return (
    <Box position="relative" {...rest}>
      {showFullscreenModal.show && (
        <FullscreenGallery
          images={imageUrls.map((item) =>
            applyImagePolicy(item, {
              mode: ImageMode.Local
            })
          )}
          initialIndex={showFullscreenModal.imageIndex}
          onClose={() =>
            setShowFullscreenModal({ ...showFullscreenModal, show: false })
          }
        />
      )}
      {modalMode ? (
        <Box position="relative">
          <ProductDetailImageBrowser
            showBullets={showBullets}
            images={imageUrls}
            breakpoint={BreakpointKey.desktop}
            imageSizeBreakpoint={BreakpointKey.tablet}
            ratio={1}
            lazy={LazyMode.AllExceptFirst}
            preload={false}
            useThumbor={useThumbor}
            imageArea={modalImageArea}
          />
          {hoverColorVariant && (
            <AbsoluteMobileBrowser>
              <ProductDetailImageBrowser
                showBullets={false}
                images={[hoverColorVariant]}
                breakpoint={BreakpointKey.desktop}
                imageSizeBreakpoint={BreakpointKey.tablet}
                ratio={1}
                lazy={LazyMode.All}
                preload={false}
                useThumbor={useThumbor}
                imageArea={modalImageArea}
              />
            </AbsoluteMobileBrowser>
          )}
          <StyledProductLabels {...productLabelsProps} modal />
          <StyledTopRightProductLabels {...topRightProductLabelsProps} modal />
        </Box>
      ) : (
        <MediaContextProvider>
          <Desktop>
            <ImageBrowserDesktop
              useThumbor={useThumbor}
              initialIndex={initialIndex}
              imageUrls={imageUrls}
              onImageClick={onImageClick}
              hoverColorVariant={hoverColorVariant}
              productLabels={
                <>
                  <StyledProductLabels {...productLabelsProps} />
                  <StyledTopRightProductLabels
                    {...topRightProductLabelsProps}
                  />
                </>
              }
            />
          </Desktop>
          <Tablet>
            <ProductDetailImageBrowser
              showBullets={false}
              images={imageUrls}
              breakpoint={BreakpointKey.tablet}
              imageSizeBreakpoint={BreakpointKey.tablet}
              onImageClick={onImageClick}
              ratio={1}
              lazy={LazyMode.AllExceptFirst}
              useThumbor={useThumbor}
              imageArea="pdp-tablet"
            />
            <StyledProductLabels {...productLabelsProps} tablet />
            <StyledTopRightProductLabels
              {...topRightProductLabelsProps}
              tablet
            />
          </Tablet>
          <Mobile>
            <ProductDetailImageBrowser
              mx="-10px"
              showBullets={true}
              images={imageUrls}
              breakpoint={BreakpointKey.mobile}
              imageSizeBreakpoint={BreakpointKey.mobile}
              onImageClick={onImageClick}
              ratio={1.5}
              lazy={LazyMode.AllExceptFirst}
              useThumbor={useThumbor}
              imageArea="pdp-mobile"
            />
            <StyledProductLabels {...productLabelsProps} mobile />
            <FictiveLeftSwipeBorder />
            <FictiveRightSwipeBorder />
            <StyledTopRightProductLabels
              {...topRightProductLabelsProps}
              mobile
            />
          </Mobile>
        </MediaContextProvider>
      )}
    </Box>
  )
}

export default ImagesBrowser

const StyledProductLabels = styled(ProductLabels)`
  bottom: ${({ mobile, modal }) => (mobile ? '30px' : modal ? '20px' : '0px')};
  left: ${({ mobile, tablet, modal }) =>
    mobile || tablet || modal ? '0px' : '24px'};
  z-index: 9;
`

const StyledTopRightProductLabels = styled(ProductLabels)`
  top: ${({ mobile }) => (mobile ? '12px' : 0)};
  right: 0;
  z-index: 9;
`

const AbsoluteMobileBrowser = styled(Box)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const FictiveSwipeBorder = css`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px;
  background: transparent;
  z-index: 5;
`

const FictiveLeftSwipeBorder = styled(Box)`
  ${FictiveSwipeBorder};
  left: -10px;
`

const FictiveRightSwipeBorder = styled(Box)`
  ${FictiveSwipeBorder};
  right: -10px;
`
